import { initials, scopeLabel, UI_TEXT } from './labels'
import type { Locale } from './logo'

/**
 * Shaffof ID ga tegadigan joylarning umumiy ko'rinishi.
 *
 * Fuqaro bir kunda bir nechta davlat tizimiga kiradi va har birida
 * «qaysi sifatda kirdim», «qanday ma'lumot so'ralyapti», «imzom qani»
 * degan narsalar boshqacha ko'rinadi. Bu uchta komponent o'sha
 * joylarni bir xil qiladi — qolgan interfeysingiz o'zingizniki.
 *
 * Uslub uchun bir marta: `import '@shaffof/id/style.css'`
 */

export type IdentityInfo = {
  /** `userinfo` dagi `name` */
  name: string
  /** `userinfo` dagi `subject_type` */
  subjectType?: 'individual' | 'legal' | null
  /** `userinfo.organization` ning maydonlari */
  organization?: string | null
  position?: string | null
  tin?: string | null
}

export type ReceiptInfo = {
  title: string
  signer?: string | null
  /** ISO-8601 yoki tayyor matn */
  signedAt?: string | null
  method?: 'eimzo' | 'face' | null
  bodyHash?: string | null
  signatureHash?: string | null
  /** `/v/{uuid}` — imzoni ochiq tekshirish sahifasi */
  verifyUrl?: string | null
}

type Options = { locale?: Locale }

/**
 * «Kim sifatida kirgansiz».
 *
 * Yuridik shaxs sertifikati bilan kirilgan bo'lsa subyekt — tashkilot:
 * odam o'z nomidan emas, tashkilot nomidan ish qilyapti va buni ko'rib
 * turishi kerak.
 */
export function createIdentityCard(identity: IdentityInfo, options: Options = {}): HTMLElement {
  const text = UI_TEXT[options.locale ?? 'uz']
  const legal = identity.subjectType === 'legal' && Boolean(identity.organization)

  const root = element('div', [
    'shaffof-id-ui',
    'shaffof-id-ui__card',
    'shaffof-id-identity',
    legal ? 'shaffof-id-identity--legal' : '',
  ])

  const avatar = element('div', ['shaffof-id-identity__avatar'])
  avatar.textContent = initials(legal ? identity.organization! : identity.name)
  avatar.setAttribute('aria-hidden', 'true')

  const body = element('div')
  const name = element('p', ['shaffof-id-identity__name'])
  const meta = element('p', ['shaffof-id-identity__meta'])

  if (legal) {
    name.textContent = `«${identity.organization}»`
    meta.textContent = [identity.name, identity.position, identity.tin && `${text.tin} ${identity.tin}`]
      .filter(Boolean)
      .join(' · ')
  } else {
    name.textContent = identity.name
    meta.textContent = text.individual
  }

  body.append(name, meta)
  root.append(avatar, body)

  return root
}

/**
 * «Bu tizim quyidagi ma'lumotlaringizni oladi».
 *
 * Tavsiflar SDK ichida: har bir tizim o'z so'zi bilan yozsa, bitta
 * `pinfl` uch xil nomlanib ketadi.
 */
export function createScopeList(scopes: string[], options: Options = {}): HTMLElement {
  const locale = options.locale ?? 'uz'
  const text = UI_TEXT[locale]

  const root = element('div', ['shaffof-id-ui', 'shaffof-id-ui__card', 'shaffof-id-scopes'])

  const title = element('p', ['shaffof-id-ui__title'])
  title.textContent = text.scopesTitle

  const list = element('ul', ['shaffof-id-scopes__list'])

  for (const scope of scopes) {
    const item = element('li', ['shaffof-id-scopes__item'])

    const tick = element('span', ['shaffof-id-scopes__tick'])
    tick.textContent = '✓'
    tick.setAttribute('aria-hidden', 'true')

    const label = element('span')
    label.textContent = scopeLabel(scope, locale)

    // `openid` — shaxsni tasdiqlashning o'zi, usiz oqim bo'lmaydi
    if (scope === 'openid') {
      const note = element('span', ['shaffof-id-scopes__note'])
      note.textContent = ` · ${text.required}`
      label.append(note)
    }

    item.append(tick, label)
    list.append(item)
  }

  root.append(title, list)

  return root
}

/**
 * Imzo kvitansiyasi.
 *
 * Hash'lar ataylab ko'rinadi: qog'ozdagi nusxa bilan solishtirish —
 * imzoni bizsiz tekshirishning eng oddiy yo'li.
 */
export function createReceiptCard(receipt: ReceiptInfo, options: Options = {}): HTMLElement {
  const text = UI_TEXT[options.locale ?? 'uz']

  const root = element('div', ['shaffof-id-ui', 'shaffof-id-ui__card', 'shaffof-id-receipt'])

  const title = element('p', ['shaffof-id-receipt__doc'])
  title.textContent = receipt.title

  const rows = element('div', ['shaffof-id-receipt__rows'])

  const add = (label: string, value?: string | null, mono = false) => {
    if (!value) return

    const row = element('div', ['shaffof-id-receipt__row'])

    const key = element('span', ['shaffof-id-receipt__label'])
    key.textContent = label

    const val = element('span', ['shaffof-id-receipt__value', mono ? 'shaffof-id-receipt__value--mono' : ''])
    val.textContent = value

    row.append(key, val)
    rows.append(row)
  }

  add(text.signedBy, receipt.signer)
  add(text.signedAt, formatDate(receipt.signedAt))
  add('', receipt.method ? text.method[receipt.method] : null)
  add(text.bodyHash, receipt.bodyHash, true)
  add(text.signatureHash, receipt.signatureHash, true)

  root.append(title, rows)

  if (receipt.verifyUrl) {
    const link = element('a', ['shaffof-id-receipt__link'])
    link.setAttribute('href', receipt.verifyUrl)
    link.setAttribute('target', '_blank')
    link.setAttribute('rel', 'noreferrer')
    link.textContent = text.verify
    root.append(link)
  }

  const note = element('p', ['shaffof-id-receipt__note'])
  note.textContent = text.receiptNote
  root.append(note)

  return root
}

/* ------------------------------------------------------------------ */

function element(tag: string, classes: string[] = []): HTMLElement {
  const node = document.createElement(tag)
  const className = classes.filter(Boolean).join(' ')

  if (className) node.className = className

  return node
}

/** ISO sanani odam o'qiydigan ko'rinishga; tayyor matn bo'lsa — o'zi */
export function formatDate(value?: string | null): string | null {
  if (!value) return null

  const date = new Date(value)

  if (Number.isNaN(date.getTime())) return value

  return date.toLocaleString('uz-UZ', {
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit',
  })
}
